import React from 'react';
import { useNavigate } from 'react-router-dom';

export const DataProcessingAddendumPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-neutral-white dark:bg-neutral-black">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/policies')}
            className="text-brand-teal hover:text-brand-teal-hover flex items-center mb-4"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Policies
          </button>
          <h1 className="text-4xl font-bold text-neutral-black dark:text-neutral-white mb-2">
            Data Processing Addendum
          </h1>
          <p className="text-sm text-neutral-medium-gray dark:text-neutral-medium-gray">
            Last Updated: November 9, 2025
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-neutral dark:prose-invert max-w-none">
          <div className="space-y-8 text-neutral-black dark:text-neutral-white">

            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Introduction and Scope</h2>
              <p className="mb-4">
                This Data Processing Addendum ("DPA") forms part of the Terms of Use between you ("Customer," "Data Controller")
                and Horwath HTL ("Processor," "we," "us," or "our") for the provision of ChatHTL services ("Services").
              </p>
              <p className="mb-4">
                This DPA applies to the processing of Personal Data (as defined in the GDPR) by Horwath HTL on behalf of
                the Customer in connection with the Services. This DPA reflects the parties' agreement with regard to the
                processing of Personal Data in accordance with the requirements of Data Protection Laws, including the GDPR
                and UK GDPR.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Definitions</h2>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>"Data Protection Laws"</strong> means all applicable laws relating to data protection, privacy, and
                  security, including the GDPR, UK GDPR, and CCPA.</li>
                <li><strong>"GDPR"</strong> means Regulation (EU) 2016/679 of the European Parliament and of the Council.</li>
                <li><strong>"Personal Data"</strong> means any information relating to an identified or identifiable natural person
                  that is processed by Horwath HTL on behalf of Customer.</li>
                <li><strong>"Data Subject"</strong> means the individual to whom Personal Data relates.</li>
                <li><strong>"Sub-processor"</strong> means any third party engaged by Horwath HTL to process Personal Data.</li>
                <li><strong>"Standard Contractual Clauses"</strong> or <strong>"SCCs"</strong> means the standard contractual
                  clauses for the transfer of personal data to third countries approved by the European Commission.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. Processing of Personal Data</h2>

              <h3 className="text-xl font-semibold mb-3 mt-6">3.1 Roles and Responsibilities</h3>
              <p className="mb-4">
                The parties acknowledge that Customer is the Data Controller and Horwath HTL is the Data Processor
                with respect to Personal Data processed under this DPA.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">3.2 Customer Instructions</h3>
              <p className="mb-4">
                Horwath HTL shall process Personal Data only on documented instructions from Customer, except where
                required by applicable law. The Services and this DPA constitute Customer's complete instructions for
                the processing of Personal Data.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">3.3 Nature and Purpose of Processing</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Nature:</strong> Provision of AI-powered chat and analysis services</li>
                <li><strong>Purpose:</strong> To provide hospitality industry insights, analysis, and assistance</li>
                <li><strong>Duration:</strong> For the term of the Services agreement and data retention period</li>
                <li><strong>Types of Personal Data:</strong> Email addresses, names, chat messages, usage data, IP addresses</li>
                <li><strong>Categories of Data Subjects:</strong> Customer's employees, contractors, and authorized users</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Processor Obligations</h2>

              <h3 className="text-xl font-semibold mb-3 mt-6">4.1 Compliance</h3>
              <p className="mb-4">
                Horwath HTL shall comply with all applicable Data Protection Laws in its processing of Personal Data.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">4.2 Confidentiality</h3>
              <p className="mb-4">
                Horwath HTL shall ensure that persons authorized to process Personal Data are subject to confidentiality
                obligations and receive appropriate training on data protection.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">4.3 Security Measures</h3>
              <p className="mb-4">
                Horwath HTL shall implement appropriate technical and organizational measures to ensure a level of
                security appropriate to the risk, including:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Encryption of Personal Data in transit and at rest (TLS 1.2+, AES-256)</li>
                <li>Regular security assessments and penetration testing</li>
                <li>Access controls and authentication mechanisms</li>
                <li>Logging and monitoring of data access and processing activities</li>
                <li>Regular backup and disaster recovery procedures</li>
                <li>Incident response and breach notification procedures</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">4.4 Sub-processing</h3>
              <p className="mb-4">
                Customer authorizes Horwath HTL to engage Sub-processors. Current Sub-processors include:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Microsoft Azure:</strong> Cloud infrastructure and data hosting (USA, Europe)</li>
                <li><strong>Stripe:</strong> Payment processing (USA)</li>
                <li><strong>OpenAI:</strong> AI model services (USA)</li>
                <li><strong>Anthropic:</strong> AI model services (USA)</li>
              </ul>
              <p className="mb-4">
                Horwath HTL shall: (a) enter into a written agreement with each Sub-processor imposing data protection
                obligations substantially similar to this DPA; (b) remain liable for Sub-processor compliance; and
                (c) provide at least 30 days' notice of any new Sub-processors, during which Customer may object.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Data Subject Rights</h2>
              <p className="mb-4">
                Horwath HTL shall, to the extent legally permitted, promptly notify Customer if it receives a request
                from a Data Subject to exercise their rights under Data Protection Laws. Taking into account the nature
                of the processing, Horwath HTL shall assist Customer by appropriate technical and organizational measures,
                insofar as possible, in fulfilling Customer's obligation to respond to requests to exercise Data Subject
                rights, including:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Right of access</li>
                <li>Right to rectification</li>
                <li>Right to erasure</li>
                <li>Right to restriction of processing</li>
                <li>Right to data portability</li>
                <li>Right to object</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Personal Data Breach</h2>
              <p className="mb-4">
                Horwath HTL shall notify Customer without undue delay (and in any event within 72 hours) after becoming
                aware of a Personal Data Breach. The notification shall include:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Description of the nature of the breach</li>
                <li>Categories and approximate number of Data Subjects affected</li>
                <li>Categories and approximate number of Personal Data records affected</li>
                <li>Likely consequences of the breach</li>
                <li>Measures taken or proposed to address the breach</li>
              </ul>
              <p className="mb-4">
                Horwath HTL shall cooperate with Customer and take reasonable steps to remediate the breach and
                prevent future breaches.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Data Protection Impact Assessment and Consultation</h2>
              <p className="mb-4">
                Horwath HTL shall provide reasonable assistance to Customer with data protection impact assessments
                and prior consultations with supervisory authorities, to the extent required by Data Protection Laws
                and taking into account the nature of processing and information available to Horwath HTL.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Deletion or Return of Personal Data</h2>
              <p className="mb-4">
                Upon termination or expiration of the Services, Horwath HTL shall, at Customer's choice:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Delete all Personal Data in its possession or control; or</li>
                <li>Return all Personal Data to Customer in a structured, commonly used, machine-readable format</li>
              </ul>
              <p className="mb-4">
                Horwath HTL may retain Personal Data to the extent required by applicable law, provided such Personal
                Data remains subject to confidentiality and security obligations.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Audit Rights</h2>
              <p className="mb-4">
                Horwath HTL shall make available to Customer all information necessary to demonstrate compliance with
                this DPA and Data Protection Laws. Horwath HTL shall allow for and contribute to audits and inspections
                by Customer or an independent auditor mandated by Customer, subject to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Reasonable advance notice (minimum 30 days)</li>
                <li>Execution of a confidentiality agreement</li>
                <li>Limitation to normal business hours</li>
                <li>No more than once per year unless required by a supervisory authority or following a breach</li>
              </ul>
              <p className="mb-4">
                Customer shall reimburse Horwath HTL for reasonable costs incurred in connection with such audits.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. International Data Transfers</h2>

              <h3 className="text-xl font-semibold mb-3 mt-6">10.1 Data Transfer Mechanisms</h3>
              <p className="mb-4">
                Personal Data may be transferred to and processed in countries outside the European Economic Area (EEA),
                including the United States. For such transfers, Horwath HTL relies on:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Standard Contractual Clauses approved by the European Commission</li>
                <li>Adequacy decisions adopted by the European Commission</li>
                <li>Other lawful transfer mechanisms under Data Protection Laws</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">10.2 Standard Contractual Clauses</h3>
              <p className="mb-4">
                The parties agree to execute the Standard Contractual Clauses (Module Two: Controller to Processor)
                as adopted by European Commission Decision 2021/914 of June 4, 2021, which are incorporated into this
                DPA by reference.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">10.3 Supplementary Measures</h3>
              <p className="mb-4">
                In addition to the SCCs, Horwath HTL implements supplementary technical and organizational measures,
                including encryption, access controls, and contractual safeguards with Sub-processors.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Limitation of Liability</h2>
              <p className="mb-4">
                Each party's and its Sub-processors' liability arising out of or related to this DPA shall be subject
                to the limitations and exclusions of liability set out in the Terms of Use. Nothing in this DPA shall
                limit or exclude either party's liability for violations of Data Protection Laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Term and Termination</h2>
              <p className="mb-4">
                This DPA shall remain in effect for the duration of the Services and shall automatically terminate
                upon cessation of all processing of Personal Data under the Services, subject to the obligations
                regarding deletion or return of Personal Data in Section 8.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">13. Governing Law and Jurisdiction</h2>
              <p className="mb-4">
                This DPA shall be governed by and construed in accordance with the governing law specified in the
                Terms of Use. For matters falling within the scope of the GDPR, the parties submit to the jurisdiction
                of the courts of the Customer's Member State.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">14. Contact Information</h2>
              <p className="mb-4">
                For questions regarding this DPA or data processing practices, please contact:
              </p>
              <p className="mb-4">
                <strong>Data Protection Officer:</strong><br />
                Email: <a href="mailto:dpo@chathtl.com" className="text-brand-teal hover:text-brand-teal-hover">dpo@chathtl.com</a>
              </p>
              <p className="mb-4">
                <strong>General Inquiries:</strong><br />
                Email: <a href="mailto:privacy@chathtl.com" className="text-brand-teal hover:text-brand-teal-hover">privacy@chathtl.com</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Annex 1: Technical and Organizational Measures</h2>

              <h3 className="text-xl font-semibold mb-3 mt-6">Access Control</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Multi-factor authentication for administrative access</li>
                <li>Role-based access control (RBAC)</li>
                <li>Regular access reviews and audits</li>
                <li>Immediate revocation of access upon employee departure</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">Encryption</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>TLS 1.2+ for data in transit</li>
                <li>AES-256 encryption for data at rest</li>
                <li>Encrypted backups</li>
                <li>Key management using Azure Key Vault</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">Network Security</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Firewall protection and network segmentation</li>
                <li>Intrusion detection and prevention systems</li>
                <li>DDoS protection</li>
                <li>Regular vulnerability scanning</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">Monitoring and Logging</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Comprehensive audit logging</li>
                <li>Security information and event management (SIEM)</li>
                <li>Real-time alerting for security events</li>
                <li>Log retention for minimum 12 months</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">Backup and Recovery</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Daily automated backups</li>
                <li>Geo-redundant backup storage</li>
                <li>Regular backup restoration testing</li>
                <li>Documented disaster recovery procedures</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">Personnel</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Background checks for employees with data access</li>
                <li>Regular data protection training</li>
                <li>Confidentiality agreements</li>
                <li>Clear data protection policies and procedures</li>
              </ul>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};
